import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useEventContext } from "Bands/contexts";
import { dataMetaSelector, mailGuestlist } from "Bands/state";
import {
  BAND_ACTIONS,
  IEmailGuestlistBlockParams,
  IEventGuestStatus,
  IGuestlistBlock,
  IGuestlistBlockStatus,
} from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { FormBuilder, Message, Modal } from "shared/components";
import { ISelectOption } from "shared/components/Form/Fields/select/types";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { Spacer } from "shared/layout";
import { IAPIState, IFormButtons, IStatus } from "shared/types";
import { getFields } from "./formData";
import { EventGuestlistBlockService } from "Bands/services";

const emailCopy = copy.guestlistBlock.email;

interface EmailGuestlistBlockModalProps {
  block: IGuestlistBlock;
  handleClose: () => void;
}

export const EmailGuestlistBlockModal: React.FC<EmailGuestlistBlockModalProps> = ({
  block,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { event } = useEventContext();
  const [nbRecipients, setNbRecipients] = useState<number>(0);
  const [nbExternals, setNbExternals] = useState<number>(0);

  const [showPendingWarning, setShowPendingWarning] = useState(false);

  const { api, data } = useSelector(dataMetaSelector(BAND_ACTIONS.createEventDetailsExport));

  useClearActionMetaOnUnMount(BAND_ACTIONS.createEventDetailsExport);

  useEffect(() => {
    if (api?.status === IAPIState.succeeded) {
      handleClose();

      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: copy.guestlistBlock.email.messages.emailAndCloseSuccess,
        })
      );
    }
  }, [api?.status, block, data, dispatch, event?.title, handleClose]);

  const handleSubmit = (data: IEmailGuestlistBlockParams) => {
    dispatch(
      mailGuestlist({
        data: {
          ...data,
          eventBlocks: [API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(block.id)],
          membersToMail: data.membersToMail
            ? (data.membersToMail as ISelectOption[]).map(({ value }) =>
                API_ENDPOINTS.BAND_USER(value)
              )
            : [],
          event: block.event.id,
        },
        blockId: block.id,
      })
    );
  };

  const totalRecipients = useMemo(() => nbRecipients + nbExternals, [nbRecipients, nbExternals]);

  const formattedButton = useMemo(() => {
    if (totalRecipients === 0) {
      if (block.status === IGuestlistBlockStatus.open) {
        return copy.event.email.emailAndClose;
      } else {
        return copy.event.email.email;
      }
    } else {
      if (block.status === IGuestlistBlockStatus.open) {
        return copy.event.email.emailRecipientsAndClose.replace("%a", `${totalRecipients}`);
      } else {
        return copy.event.email.emailRecipients.replace("%a", `${totalRecipients}`);
      }
    }
  }, [totalRecipients, block.status]);

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        children: formattedButton,
        disabled: api?.status === IAPIState.pending || totalRecipients === 0,
      },
    }),
    [formattedButton, api?.status, totalRecipients]
  );

  const defaultValues = useMemo(
    () => ({
      title: block.title,
    }),
    [block.title]
  );

  useEffect(() => {
    void (async () => {
      const data = await EventGuestlistBlockService.getGuests({
        guestListBlock: API_ENDPOINTS.EVENT_GUESTLIST_BLOCK(block.id),
        status: IEventGuestStatus.pending,
      });

      setShowPendingWarning(data?.pagination?.totalItems > 0);
    })();
  }, [block.id]);

  return (
    <Modal
      title={emailCopy.title.replace("%b", block.title)}
      dataTestId="email_guestlist_modal"
      handleClose={handleClose}
    >
      <Spacer size="m">
        {showPendingWarning && (
          <Message status={IStatus.warning}>
            <strong style={{ fontWeight: "bold" }}>
              {copy.guestlistBlock.download.messages.pendingRequestsTitle}
            </strong>
            <p>{copy.guestlistBlock.download.messages.pendingRequestsMessage}</p>
          </Message>
        )}
        {block.status === IGuestlistBlockStatus.closed && (
          <Message message={copy.guestlistBlock.view.messages.closed} />
        )}
        <FormBuilder
          fields={getFields({ setNbRecipients, setNbExternals })}
          onSubmit={handleSubmit}
          buttons={buttons}
          defaultValues={defaultValues}
          isSubmitting={api?.status === IAPIState.pending}
          apiError={api?.error}
          apiErrorMessage={!!api?.error}
          globalInfo={copy.guestlistBlock.email.messages.emailAttachment}
        />
      </Spacer>
    </Modal>
  );
};
